import { useTranslation } from '@silospot/i18n/react';
import type { CloudflareCapabilities, CloudflareZoneOption } from '@silospot/shared';
import { toast } from '@silospot/ui';
import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, Check, Cloud, Info, KeyRound, Loader2 } from 'lucide-react';
import { type ReactElement, useState } from 'react';
import { api, serverError } from '../../lib/api';
import { Badge } from './kit';

/** Cloudflare "create token via URL" template — pre-checks the 5 permission
 *  groups of the COMBINED Silospot token. One token covers both capabilities:
 *  Zone:Read, Zone WAF:Edit, Firewall Services:Edit, Account Rule Lists
 *  (= Account Filter Lists):Edit for edge blocking, plus Zaraz:Edit for tracker
 *  injection. The client creates this single token whether they start from
 *  blocking or injection; activation stays progressive (blocking is inert until
 *  explicitly enabled). The zone resource still has to be narrowed to the site
 *  by hand — the template can't set a specific zone. */
const CF_PERMS = encodeURIComponent(
  JSON.stringify([
    { key: 'zone', type: 'read' },
    { key: 'zone_waf', type: 'edit' },
    { key: 'firewall_services', type: 'edit' },
    { key: 'account_rule_lists', type: 'edit' },
    { key: 'zaraz', type: 'edit' },
  ]),
);
export const CF_TOKEN_URL = `https://dash.cloudflare.com/profile/api-tokens?permissionGroupKeys=${CF_PERMS}&accountId=*&zoneId=all&name=${encodeURIComponent('Silospot — Cloudflare')}`;

type CheckState = null | 'checking' | 'invalid' | 'noperm' | 'ok';
type Mode = 'manual' | 'challenge' | 'auto';

const STEPS = ['cf.stepWhy', 'cf.stepToken', 'cf.stepPaste', 'cf.stepConfig'];

export function CfFlow({
  projectId,
  projectDomain,
  onDone,
  connectOnly = false,
}: {
  projectId: string;
  projectDomain: string;
  onDone: () => void;
  /** Tracker-injection variant: skip the blocking "why"/config steps — only create
   *  & validate the combined token and persist the connection (blocking stays
   *  manual/inert), then hand back to the caller. Default false = full setup. */
  connectOnly?: boolean;
}): ReactElement {
  const { t } = useTranslation('pages');
  const [step, setStep] = useState(connectOnly ? 1 : 0);
  const [token, setToken] = useState('');
  const [check, setCheck] = useState<CheckState>(null);
  const [caps, setCaps] = useState<CloudflareCapabilities | null>(null);
  const [zoneId, setZoneId] = useState('');
  const [mode, setMode] = useState<Mode>('manual');
  const [autoAck, setAutoAck] = useState(false);
  const [analysis, setAnalysis] = useState<{
    toAdd: number;
    afterCidr: number;
    total: number;
    placement: string;
  } | null>(null);
  const [synced, setSynced] = useState(false);

  const missingPerm = (c: CloudflareCapabilities): string | null => {
    if (c.missing.includes('account_filter_lists')) return t('cf.permList');
    if (c.missing.includes('firewall_services')) return t('cf.permFw');
    if (c.missing.includes('zone_read')) return t('cf.permZone');
    return null;
  };

  const validate = useMutation({
    mutationFn: () => api.cfDetect(projectId, token.trim()),
    onMutate: () => setCheck('checking'),
    onSuccess: (c) => {
      setCaps(c);
      if (!c.tokenValid) {
        setCheck('invalid');
        return;
      }
      if (missingPerm(c)) {
        setCheck('noperm');
        return;
      }
      setCheck('ok');
      setZoneId(c.matchedZone?.id ?? c.zones[0]?.id ?? '');
    },
    onError: () => setCheck('invalid'),
  });

  const analyze = useMutation({
    mutationFn: async () => {
      await api.cfConnect(projectId, { token: token.trim(), zoneId, mode, manualAllowed: true });
      const prov = await api.cfProvision(projectId, false);
      const sync = await api.cfSync(projectId, false);
      return { prov, sync };
    },
    onSuccess: ({ prov, sync }) => {
      setAnalysis({
        toAdd: sync.plan.toAdd,
        afterCidr: sync.plan.afterCidr,
        total: sync.plan.candidatesTotal,
        placement:
          prov.plan.mode === 'fallback' ? t('cf.placementFallback') : t('cf.placementList'),
      });
      toast.success(t('cf.analyzeDone'));
    },
    onError: (e) => toast.error(serverError(e) ?? t('cf.errorGeneric')),
  });

  const sync = useMutation({
    mutationFn: async () => {
      await api.cfProvision(projectId, true);
      return api.cfSync(projectId, true);
    },
    onSuccess: () => {
      setSynced(true);
      toast.success(t('cf.syncDone'));
    },
    onError: (e) => toast.error(serverError(e) ?? t('cf.errorGeneric')),
  });

  // connectOnly (tracker injection): persist the token + zone with blocking left
  // in manual (inert), then hand back — the caller activates the edge injection.
  const connectInjection = useMutation({
    mutationFn: () =>
      api.cfConnect(projectId, {
        token: token.trim(),
        zoneId,
        mode: 'manual',
        manualAllowed: true,
      }),
    onSuccess: () => onDone(),
    onError: (e) => toast.error(serverError(e) ?? t('cf.errorGeneric')),
  });

  const finish = () => {
    toast.success(t('cf.activated'), { description: projectDomain });
    onDone();
  };

  const zones: CloudflareZoneOption[] = caps?.zones ?? [];

  return (
    <div>
      {/* Stepper — connectOnly shows only the token + paste steps (no blocking). */}
      <div className="mb-6 flex flex-wrap items-center gap-2">
        {(connectOnly ? STEPS.slice(1, 3) : STEPS).map((s, idx) => {
          const i = connectOnly ? idx + 1 : idx;
          return (
            <div
              key={s}
              className={`flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-[12.5px] ${i === step ? 'bg-[#fbead0] font-semibold text-[#b5560f]' : i < step ? 'text-text-2' : 'text-text-3'}`}
            >
              <span
                className={`grid h-5 w-5 place-items-center rounded-full text-[10.5px] ${i < step ? 'bg-clean-fg text-white' : i === step ? 'bg-[#f6821f] text-white' : 'bg-neutral-bg text-text-3'}`}
              >
                {i < step ? <Check className="h-3 w-3" /> : idx + 1}
              </span>
              {t(s)}
            </div>
          );
        })}
      </div>

      {step === 0 ? (
        <div>
          <h2 className={h2}>{t('cf.whyTitle')}</h2>
          <p className={lead}>{t('cf.whyLead')}</p>
          <div className="mb-4 flex flex-col gap-2.5">
            {(
              [
                ['cf.whyAutoT', 'cf.whyAutoB'],
                ['cf.whyManualT', 'cf.whyManualB'],
                ['cf.whyRulesT', 'cf.whyRulesB'],
              ] as const
            ).map(([tk, bk]) => (
              <div
                key={tk}
                className="flex items-center gap-3 rounded-xl border border-border bg-bg/40 px-3.5 py-2.5"
              >
                <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-[#fbead0] text-[#f6821f]">
                  <Cloud className="h-4 w-4" />
                </span>
                <div>
                  <div className="text-[13.5px] font-semibold text-text">{t(tk)}</div>
                  <div className="text-[12px] text-text-3">{t(bk)}</div>
                </div>
              </div>
            ))}
          </div>
          <Callout tone="info" icon={<Info className="h-4 w-4 shrink-0 text-primary" />}>
            {t('cf.proxyPrereq', { domain: projectDomain })}
          </Callout>
          <div className="mt-6 flex justify-end">
            <button type="button" onClick={() => setStep(1)} className={btnPrimary}>
              {t('cf.continue')}
            </button>
          </div>
        </div>
      ) : step === 1 ? (
        <div>
          <h2 className={h2}>{t('cf.tokenTitle')}</h2>
          <p className={lead}>{t('cf.tokenLead')}</p>
          <a
            href={CF_TOKEN_URL}
            target="_blank"
            rel="noreferrer"
            className="mb-3 inline-flex items-center gap-2 rounded-[10px] bg-[#f6821f] px-4 py-2.5 text-[13.5px] font-semibold text-white hover:opacity-90"
          >
            <Cloud className="h-4 w-4" /> {t('cf.openCf')}
          </a>
          <Callout tone="info" icon={<Info className="h-4 w-4 shrink-0 text-primary" />}>
            {t('cf.tokenHelp')}
          </Callout>
          <div className="mt-6 flex items-center justify-between">
            {connectOnly ? (
              <span />
            ) : (
              <button type="button" onClick={() => setStep(0)} className={btnGhost}>
                {t('cf.back')}
              </button>
            )}
            <button type="button" onClick={() => setStep(2)} className={btnPrimary}>
              {t('cf.haveToken')}
            </button>
          </div>
        </div>
      ) : step === 2 ? (
        <div>
          <h2 className={h2}>{t('cf.pasteTitle')}</h2>
          <p className={lead}>{t('cf.pasteLead')}</p>
          <div className="flex items-center overflow-hidden rounded-[10px] border border-border-strong">
            <span className="bg-bg px-3 py-2.5 text-text-3">
              <KeyRound className="h-4 w-4" />
            </span>
            <input
              value={token}
              onChange={(e) => {
                setToken(e.target.value);
                setCheck(null);
              }}
              placeholder={t('cf.pastePlaceholder')}
              className="w-full px-3 py-2.5 font-mono text-[13px] outline-none"
            />
            <button
              type="button"
              disabled={!token.trim() || validate.isPending}
              onClick={() => validate.mutate()}
              className="m-1 rounded-md bg-primary px-3 py-1.5 text-[12.5px] font-semibold text-white hover:opacity-90 disabled:opacity-50"
            >
              {t('cf.validate')}
            </button>
          </div>

          {check === 'checking' ? (
            <div className="mt-3 flex items-center gap-2 text-[13px] text-text-3">
              <Loader2 className="h-4 w-4 animate-spin" /> {t('cf.checking')}
            </div>
          ) : null}
          {check === 'invalid' ? (
            <Callout
              tone="warn"
              className="mt-3"
              icon={<AlertTriangle className="h-4 w-4 shrink-0 text-[#b45309]" />}
            >
              {t('cf.invalidToken')}
            </Callout>
          ) : null}
          {check === 'noperm' && caps ? (
            <Callout
              tone="warn"
              className="mt-3"
              icon={<AlertTriangle className="h-4 w-4 shrink-0 text-[#b45309]" />}
            >
              {t('cf.missingPerm', { perm: missingPerm(caps) ?? '' })}
            </Callout>
          ) : null}
          {check === 'ok' && caps ? (
            <div className="mt-3">
              <div className="mb-3 flex items-start gap-2.5 rounded-xl border border-clean-border bg-clean-bg px-3.5 py-3 text-clean-fg">
                <Check className="mt-0.5 h-4 w-4 shrink-0" />
                <div>
                  <div className="text-[13.5px] font-semibold">{t('cf.tokenOk')}</div>
                  <div className="text-[11.5px] text-text-3">
                    {t('cf.zonesDetected', { count: zones.length })}
                  </div>
                </div>
              </div>
              <div className="mb-1.5 text-[12px] font-semibold text-text-2">{t('cf.pickZone')}</div>
              <div className="flex flex-col gap-1.5">
                {zones.map((z) => (
                  <button
                    type="button"
                    key={z.id}
                    onClick={() => setZoneId(z.id)}
                    className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 text-left ${zoneId === z.id ? 'border-clean-fg bg-clean-bg' : 'border-border hover:bg-bg'}`}
                  >
                    <span className="grid h-7 w-7 place-items-center rounded-lg bg-[#fbead0] text-[#f6821f]">
                      <Cloud className="h-4 w-4" />
                    </span>
                    <div className="flex-1">
                      <div className="text-[13.5px] font-semibold text-text">{z.name}</div>
                      <div className="text-[11.5px] text-text-3">
                        {z.name === projectDomain ? t('cf.zoneMatch') : t('cf.zoneOther')}
                        {z.active ? '' : ` · ${t('cf.zoneInactive')}`}
                      </div>
                    </div>
                    {zoneId === z.id ? <Check className="h-4 w-4 text-clean-fg" /> : null}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          <div className="mt-6 flex items-center justify-between">
            <button type="button" onClick={() => setStep(1)} className={btnGhost}>
              {t('cf.back')}
            </button>
            <button
              type="button"
              disabled={check !== 'ok' || !zoneId || connectInjection.isPending}
              onClick={() => (connectOnly ? connectInjection.mutate() : setStep(3))}
              className={btnPrimary}
            >
              {connectInjection.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {connectOnly ? t('cf.connectActivate') : t('cf.continue')}
            </button>
          </div>
        </div>
      ) : (
        <div>
          <h2 className={h2}>{t('cf.configTitle')}</h2>
          <p className={lead}>{t('cf.configLead')}</p>

          <div className="rounded-xl border border-border bg-bg/40 p-3.5">
            <div className="mb-1.5 text-[12px] font-semibold text-text-2">{t('cf.modeLabel')}</div>
            <select
              value={mode}
              onChange={(e) => {
                setMode(e.target.value as Mode);
                setAutoAck(false);
              }}
              className="w-full rounded-[10px] border border-border-strong px-3 py-2.5 text-[13.5px] outline-none"
            >
              <option value="manual">{t('cf.modeManual')}</option>
              <option value="challenge">{t('cf.modeChallenge')}</option>
              <option value="auto">{t('cf.modeAuto')}</option>
            </select>

            {mode === 'auto' ? (
              <Callout
                tone="warn"
                className="mt-3"
                icon={<AlertTriangle className="h-4 w-4 shrink-0 text-[#b45309]" />}
              >
                <div>
                  <div className="font-semibold">{t('cf.autoWarnTitle')}</div>
                  <p className="mt-0.5">{t('cf.autoWarnBody')}</p>
                  <label className="mt-2 flex items-center gap-2 text-[12.5px] font-semibold text-text">
                    <input
                      type="checkbox"
                      checked={autoAck}
                      onChange={(e) => setAutoAck(e.target.checked)}
                    />
                    {t('cf.autoAck')}
                  </label>
                </div>
              </Callout>
            ) : (
              <p className="mt-2 text-[12px] text-text-3">{t('cf.modeHint')}</p>
            )}
          </div>

          <div className="mt-4 rounded-xl border border-border bg-bg/40 p-3.5">
            <div className="text-[12.5px] font-semibold text-text">{t('cf.syncTitle')}</div>
            <p className="mt-0.5 text-[12px] text-text-3">{t('cf.syncHint')}</p>
            <div className="mt-3 flex flex-col items-start gap-2">
              {!analysis ? (
                <button
                  type="button"
                  disabled={analyze.isPending}
                  onClick={() => analyze.mutate()}
                  className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-[12.5px] font-semibold text-text-2 hover:bg-bg disabled:opacity-50"
                >
                  {analyze.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {t('cf.analyze')}
                </button>
              ) : (
                <>
                  <span className="text-[12px] text-text-2">
                    {t('cf.analysisResult', {
                      total: analysis.total,
                      after: analysis.afterCidr,
                      placement: analysis.placement,
                    })}
                  </span>
                  {synced ? (
                    <Badge tone="clean" dot>
                      {t('cf.syncedBadge', { count: analysis.toAdd })}
                    </Badge>
                  ) : (
                    <button
                      type="button"
                      disabled={sync.isPending}
                      onClick={() => sync.mutate()}
                      className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-[12.5px] font-semibold text-text-2 hover:bg-bg disabled:opacity-50"
                    >
                      {sync.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                      {t('cf.syncNow')}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="mt-6 flex items-center justify-between">
            <button type="button" onClick={() => setStep(2)} className={btnGhost}>
              {t('cf.back')}
            </button>
            <button
              type="button"
              disabled={(mode === 'auto' && !autoAck) || analyze.isPending || sync.isPending}
              onClick={finish}
              className="inline-flex items-center gap-2 rounded-[10px] bg-[#f6821f] px-4 py-2.5 text-[13.5px] font-semibold text-white hover:opacity-90 disabled:opacity-50"
            >
              <Cloud className="h-4 w-4" /> {t('cf.activate')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Callout({
  tone,
  icon,
  children,
  className = '',
}: {
  tone: 'info' | 'warn';
  icon: ReactElement;
  children: React.ReactNode;
  className?: string;
}): ReactElement {
  const tones =
    tone === 'warn'
      ? 'border-[#f3dca6] bg-[#fdf6e8] text-[#7c5212]'
      : 'border-[#cfe0ff] bg-accent-bg text-text-2';
  return (
    <div
      className={`flex items-start gap-2.5 rounded-xl border px-3.5 py-3 text-[12.5px] leading-relaxed ${tones} ${className}`}
    >
      {icon}
      <div>{children}</div>
    </div>
  );
}

const btnPrimary =
  'inline-flex items-center gap-2 rounded-[10px] bg-primary px-4 py-2.5 text-[13.5px] font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50';
const btnGhost =
  'inline-flex items-center gap-2 rounded-[10px] px-3 py-2.5 text-[13.5px] font-semibold text-text-2 hover:bg-bg disabled:opacity-40';
const h2 = 'text-[18px] font-bold tracking-[-0.02em] text-text';
const lead = 'mb-4 mt-1.5 max-w-xl text-[13.5px] leading-relaxed text-text-2';
